import { ArrowTopRightOnSquareIcon, CloudArrowDownIcon } from '@heroicons/react/20/solid';
import { XMarkIcon } from '@heroicons/react/24/solid';
import { useCallback, useEffect, useRef, useState } from 'react';
import Draggable, { DraggableData, DraggableEvent } from 'react-draggable';
import { useDispatch, useSelector } from 'react-redux';
import { useFirmContext } from '../../providers/FirmProvider';
import { RootState } from '../../store';
import { clearViewingDocument } from '../../slices/documentViewSlice';
import { FileRecordTypeEnum } from '../../types/file-record';
import { FYI_URL } from '../../config';
import { toastNotification } from '../toast/Toast';
import { ButtonV2 } from '@cimplico/components';
import FYILogo from '../../assets/fyi.png';
import DocumentRenderer from './DocumentRenderer';
import { useDownloadLink } from '../../queries/use-download-link';
import { downloadDocument } from '../../utilities/document-download';

type DocumentBrowserProps = {
    locationSaveKey: string;
};

function DocumentBrowser({ locationSaveKey }: DocumentBrowserProps): JSX.Element | null {
    const ref = useRef<HTMLDivElement | null>(null);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const dispatch = useDispatch();
    const record = useSelector((state: RootState) => state.documentView.value);
    const { shortId } = useFirmContext();
    const downloadQuery = useDownloadLink(record ? [record.id] : [], false);

    const isFYI = (record?.source ?? record)?.recordType === FileRecordTypeEnum.FYI_DOCUMENT;
    const metadata = (record?.source ?? record)?.metadata;

    function handleStop(event: DraggableEvent, { x, y }: DraggableData): void {
        setPosition({ x, y });
    }

    const close = useCallback(
        function close(): void {
            dispatch(clearViewingDocument());
        },
        [dispatch]
    );

    async function download(): Promise<void> {
        try {
            const res = await downloadQuery.refetch();
            const urls = res.data?.urls ?? [];

            const url = urls[0]?.url;

            if (url && record) {
                downloadDocument(record?.name, url);
            }
        } catch (error) {
            toastNotification('error', 'Unable to download document. Something went wrong');
        }
    }

    useEffect(() => {
        return (): void => {
            close();
        };
    }, [close]);

    if (!record) {
        return null;
    }

    return (
        <Draggable handle="#handle" nodeRef={ref} onStop={handleStop} position={position}>
            {/* Container */}
            <div
                className="min-h-[700px] min-w-[550px] fixed z-[999] bg-white border shadow-lg rounded-md flex flex-col resize overflow-auto"
                ref={ref}
            >
                {/* Header bar */}
                <div className="mt-2 pb-2 border-b px-4 flex items-center justify-between gap-2 cursor-move" id="handle">
                    <div className="flex items-center w-full">
                        <div className="flex-1">
                            <p className="text-gray-700 text-sm max-w-[1/2] line-clamp-1 break-all font-bold">{record.name}</p>
                        </div>
                        <div className="flex items-center mr-2">
                            {isFYI && (
                                <>
                                    <a
                                        className="text-sm text-gray-600 flex items-center hover:underline pr-2 mr-2 border-r"
                                        href={`${FYI_URL}/documents/${metadata?.id}/preview`}
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        <img src={FYILogo} className="h-5 w-5 mr-1" />
                                        View in FYI
                                    </a>
                                </>
                            )}
                            <a href={`/${shortId}/document-view/${record.id}`} target="_blank" rel="noreferrer">
                                <ArrowTopRightOnSquareIcon className="h-4 w-4 hover:cursor-pointer mr-1" />{' '}
                            </a>
                            <ButtonV2 size="sm" type="hollow" leftIcon={<CloudArrowDownIcon />} label="Download" onClick={download} />
                        </div>
                        <button onClick={close}>
                            <XMarkIcon className="h-4 w-4" />
                        </button>
                    </div>
                </div>
                <DocumentRenderer record={record} onDownload={download} />

                {/* Bottom actions */}
                <div className="border-t h-9"></div>
            </div>
        </Draggable>
    );
}
export default DocumentBrowser;
